import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ArrowRight, Clock, FileText, Shield, ChevronRight, Plane, Globe, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LandingPage = () => {
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API}/countries`);
      setCountries(response.data.countries);
    } catch (error) {
      console.error("Error fetching countries:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCountrySelect = (countryCode) => {
    setSelectedCountry(countryCode);
    if (countryCode) {
      navigate(`/visa/${countryCode}`);
    }
  };

  const features = [
    {
      icon: <FileText className="w-6 h-6" />,
      title: "Visa Requirements",
      description: "Access complete document requirements for any country"
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Processing Times",
      description: "Know exactly how many days it takes to process your visa"
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Official Links",
      description: "Direct links to official embassy and visa portals"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Photo Specifications",
      description: "Detailed photo requirements for each country"
    }
  ];

  const popularDestinations = [
    { code: "US", name: "United States", flag: "🇺🇸", processingDays: 15 },
    { code: "GB", name: "United Kingdom", flag: "🇬🇧", processingDays: 15 },
    { code: "CA", name: "Canada", flag: "🇨🇦", processingDays: 20 },
    { code: "AU", name: "Australia", flag: "🇦🇺", processingDays: 20 },
    { code: "DE", name: "Germany", flag: "🇩🇪", processingDays: 15 },
    { code: "JP", name: "Japan", flag: "🇯🇵", processingDays: 5 },
  ];

  return (
    <div className="min-h-screen" data-testid="landing-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 hero-pattern">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Content */}
            <div className="lg:col-span-7 space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#E15636]/10 rounded-full">
                <Plane className="w-4 h-4 text-[#E15636]" />
                <span className="text-sm font-medium text-[#E15636]">Your Visa Journey Starts Here</span>
              </div>
              
              <h1 className="text-5xl md:text-7xl font-heading tracking-tight text-[#0F172A] leading-[1.1]">
                Visa Information
                <span className="block text-[#E15636]">Made Simple</span>
              </h1>
              
              <p className="text-lg md:text-xl text-[#64748B] leading-relaxed max-w-xl">
                Access comprehensive visa requirements, processing times, and official application links 
                for destinations worldwide. Your trusted guide for international travel.
              </p>

              {/* Country Dropdown */}
              <div className="max-w-md" data-testid="country-search">
                <label className="block text-sm font-medium text-[#0F172A] mb-2">
                  Select your destination country
                </label>
                <Select value={selectedCountry} onValueChange={handleCountrySelect}>
                  <SelectTrigger 
                    className="h-14 rounded-full border-2 border-slate-200 focus:border-[#0F172A] text-base px-6"
                    data-testid="destination-dropdown"
                  >
                    <div className="flex items-center gap-2">
                      <Search className="w-5 h-5 text-[#64748B]" />
                      <SelectValue placeholder="Choose destination country..." />
                    </div>
                  </SelectTrigger>
                  <SelectContent className="max-h-80">
                    {countries.map((country) => (
                      <SelectItem 
                        key={country.code} 
                        value={country.code}
                        data-testid={`country-option-${country.code}`}
                      >
                        <span className="flex items-center gap-3">
                          <span className="text-xl">{country.flag}</span>
                          <span>{country.name}</span>
                          {country.is_visa_on_arrival && (
                            <span className="ml-2 text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">VOA</span>
                          )}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-wrap gap-4">
                <Link to="/visa-finder" data-testid="visa-finder-btn">
                  <Button className="h-12 px-8 rounded-full bg-[#0F172A] text-white hover:bg-[#1E293B] shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300">
                    Start Visa Finder
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/terms" data-testid="terms-btn">
                  <Button variant="outline" className="h-12 px-8 rounded-full border-2 border-[#0F172A] text-[#0F172A] hover:bg-[#0F172A] hover:text-white transition-all">
                    Terms & Conditions
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right Content - Hero Image */}
            <div className="lg:col-span-5 relative">
              <div className="relative aspect-square max-w-md mx-auto">
                <img
                  src="https://images.unsplash.com/photo-1488085061387-422e29b40080?w=600&q=80"
                  alt="Travel"
                  className="rounded-2xl object-cover w-full h-full shadow-2xl"
                />
                <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                      <Shield className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[#0F172A]">16 Countries</p>
                      <p className="text-xs text-[#64748B]">Visa Information</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-heading tracking-tight text-[#0F172A] mb-4">
              Everything You Need
            </h2>
            <p className="text-lg text-[#64748B] max-w-2xl mx-auto">
              From visa requirements to official application portals, we provide comprehensive information 
              for your travel planning.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group relative overflow-hidden rounded-2xl bg-white p-8 shadow-sm transition-all hover:shadow-md border border-slate-200 hover:border-[#E15636]/30"
                data-testid={`feature-${index}`}
              >
                <div className="w-12 h-12 bg-[#E15636]/10 rounded-xl flex items-center justify-center text-[#E15636] mb-6 group-hover:bg-[#E15636] group-hover:text-white transition-all">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-heading text-[#0F172A] mb-2">{feature.title}</h3>
                <p className="text-[#64748B] text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Destinations */}
      <section className="py-20 md:py-32 bg-[#F8FAFC]" data-testid="destinations-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12 gap-4">
            <div>
              <h2 className="text-3xl md:text-5xl font-heading tracking-tight text-[#0F172A] mb-2">
                Popular Destinations
              </h2>
              <p className="text-[#64748B]">Most searched visa destinations</p>
            </div>
            <Link to="/visa-finder" data-testid="view-all-countries">
              <Button variant="outline" className="rounded-full border-2 border-[#0F172A] text-[#0F172A] hover:bg-[#0F172A] hover:text-white">
                View All Countries
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {popularDestinations.map((dest) => (
              <Link
                key={dest.code}
                to={`/visa/${dest.code}`}
                className="country-card group bg-white rounded-2xl p-6 border border-slate-200 hover:border-[#E15636]/30"
                data-testid={`destination-${dest.code}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <span className="text-5xl">{dest.flag}</span>
                  <div className="flex items-center gap-1 text-sm text-[#64748B]">
                    <Clock className="w-4 h-4" />
                    <span>{dest.processingDays} days</span>
                  </div>
                </div>
                <h3 className="text-xl font-heading text-[#0F172A] mb-1">{dest.name}</h3>
                <p className="text-sm text-[#64748B] mb-4">Click to view visa requirements</p>
                <div className="flex items-center text-[#E15636] font-medium text-sm group-hover:gap-2 transition-all">
                  <span>View Details</span>
                  <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-[#0F172A]" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-3xl md:text-5xl font-heading tracking-tight text-white mb-6">
            Ready to Start Your <span className="text-[#E15636]">Visa Journey</span>?
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-8">
            Find visa requirements and get direct links to official embassy portals 
            for your destination country.
          </p>
          <Link to="/visa-finder" data-testid="cta-visa-finder">
            <Button className="h-14 px-10 rounded-full bg-[#E15636] text-white hover:bg-[#C24125] shadow-lg hover:shadow-xl text-lg">
              Find Your Visa Requirements
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
